/**
 * Under a meeting's *When* — `docs/activity.md` §9.6.
 *
 * **One person** (Board 11 F): *<First name>’s <Dow>* and the cities of the
 * day, the person's day 8 AM–4 PM with the stops, the new meeting green and
 * dashed-edged, and under it the drive to it — *14 min from Meridian · fits*,
 * amber when tight, red when short — with **Open the calendar**.
 *
 * **Several** (Board 20 A, the time finder): *Everyone’s <Weekday>*, a row per
 * person going, 7 AM–noon by default, each stop in the person's tones with
 * its short name and the **estimated drive before it as a dotted stretch**,
 * time before work hatched *before work*, and the chosen time a green-ringed
 * column labelled *All <n> free · <from>–<to>*. **Next free time ›** jumps to
 * the next span everyone is free for the length.
 *
 * Every busy span, drive and free answer is the server's (`/activity/free`,
 * `/activity/meeting-fit` — estimates, D7); this only lays them out.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  BLOCK_TYPE_LABELS, KIND_STYLES, clockBare, finderWords, hourLabel, shortStopName, toneOf, weekdayLong,
  type BlockType, type CalendarItem, type CalendarPersonDays, type MeetingFit,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'

/** The drive before a stop: vertical stripes, faint (Board 20 A). */
const DRIVE_STRIPES = 'repeating-linear-gradient(90deg,rgb(var(--color-line-strong)),rgb(var(--color-line-strong)) 2px,transparent 2px,transparent 5px)'
/** Before work: a faint diagonal hatch (Board 20 A). */
const BEFORE_WORK = 'repeating-linear-gradient(-45deg,rgba(96,94,84,.06),rgba(96,94,84,.06) 4px,transparent 4px,transparent 8px)'

const minutesOf = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

/** The hours shown: `span` hours from `from`, moved to hold the chosen time when it falls outside. */
function windowFor(from: number, span: number, start: number, end: number): { first: number; last: number } {
  let first = from
  if (start < first * 60 || end > (first + span) * 60) first = Math.max(0, Math.min(24 - span, Math.floor(start / 60) - 1))
  return { first, last: first + span }
}

function useWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>
  const [width, setWidth] = useState(540)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return undefined
    /* jsdom lays nothing out: a box of no width keeps the board's 540. */
    const measure = () => setWidth(node.clientWidth || 540)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const watcher = new ResizeObserver(measure)
    watcher.observe(node)
    return () => watcher.disconnect()
  }, [])
  return [ref, width]
}

const busyItems = (person: CalendarPersonDays | undefined): CalendarItem[] =>
  (person?.days[0]?.items ?? []).filter((item) => !item.allDay && item.kind !== 'task')
    .sort((a, b) => a.start - b.start)

/** *Harrisburg, PA, then Camp Hill, PA* — the day's cities in order, the new place among them. */
export function citiesWords(items: readonly CalendarItem[], extra: { start: number; city: string | null } | null): string {
  const places = items.filter((item) => item.place?.city && !item.remote)
    .map((item) => ({ start: item.start, city: `${item.place!.city}${item.place!.state ? `, ${item.place!.state}` : ''}` }))
  if (extra?.city) places.push({ start: extra.start, city: extra.city })
  places.sort((a, b) => a.start - b.start)
  const out: string[] = []
  for (const place of places) if (out[out.length - 1] !== place.city) out.push(place.city)
  return out.join(', then ')
}

const DRIVE_TONE = { ok: 'text-text-muted', tight: 'text-warn', short: 'text-danger' } as const

/** Board 11 F: one person's day and where the new meeting falls in it. */
export function DayStrip({
  person, date, start, minutes, label, city, fit, onOpenCalendar,
}: {
  person: CalendarPersonDays | undefined
  date: string
  start: number
  minutes: number
  /** *1:30 Dana* — the time and who it is with (else the title). */
  label: string
  /** The new meeting's *City, ST*, for the cities line. */
  city: string | null
  fit: MeetingFit | null
  onOpenCalendar: () => void
}) {
  const [track, width] = useWidth()
  const end = start + minutes
  const { first, last } = windowFor(8, 8, start, end)
  const scale = width / ((last - first) * 60)
  const x = (m: number) => (m - first * 60) * scale
  const items = busyItems(person)
  const firstName = person?.firstName ?? ''
  const newLeft = x(start)

  return (
    <div className="rounded-[10px] border border-line-faint bg-surface-alt px-3 pb-1 pt-2.5">
      <div className="flex items-center gap-2">
        <b className="whitespace-nowrap text-[12px]">{firstName}’s {weekdayLong(date)}</b>
        <span className="truncate text-[11.5px] text-text-muted">{citiesWords(items, { start, city })}</span>
        <span className="flex-1" />
        <button type="button" onClick={onOpenCalendar} className="whitespace-nowrap text-[11.5px] font-semibold text-accent">
          Open the calendar
        </button>
      </div>
      <div className="h-2" />
      <div ref={track} className="relative mx-2 h-[76px]">
        {Array.from({ length: last - first + 1 }, (_, n) => (
          <i key={n} className="absolute top-0 h-[42px] border-l border-line-faint" style={{ left: n * 60 * scale }} />
        ))}
        {items.filter((item) => item.end > first * 60 && item.start < last * 60).map((item) => {
          const edge = item.kind === 'appointment' ? KIND_STYLES.site_visit.fg
            : item.kind === 'meeting' ? KIND_STYLES.meeting.fg : KIND_STYLES.block.fg
          return (
            <div key={item.key}
              className="absolute top-1.5 h-[30px] overflow-hidden whitespace-nowrap rounded-md border border-line bg-surface px-1.5 py-0.5 text-[10.5px] font-semibold"
              style={{ left: x(Math.max(item.start, first * 60)), width: Math.max(18, (Math.min(item.end, last * 60) - Math.max(item.start, first * 60)) * scale - 3), borderLeft: `3px solid ${edge}` }}>
              {item.kind === 'block' && item.blockType ? BLOCK_TYPE_LABELS[item.blockType as BlockType] ?? item.title : shortStopName(item.title)}
            </div>
          )
        })}
        <div className="absolute top-1 h-[34px] overflow-hidden whitespace-nowrap rounded-md border-2 border-accent bg-accent-soft px-1.5 py-0.5 text-[10.5px] font-bold text-accent-dark"
          style={{ left: newLeft, width: Math.max(24, minutes * scale - 3) }}>
          {label}
        </div>
        {fit?.drive && (
          <div className={cn('absolute top-[44px] flex items-center gap-[3px] whitespace-nowrap text-[10.5px] font-semibold', DRIVE_TONE[fit.drive.tone])}
            style={{ left: Math.max(0, Math.min(newLeft - 40, width - 200)) }}>
            <Glyph name="car" size={11} />{fit.drive.words}
          </div>
        )}
        {Array.from({ length: Math.floor((last - first) / 2) + 1 }, (_, n) => first + n * 2).map((hour) => (
          <span key={hour} className="absolute top-[62px] -translate-x-1/2 whitespace-nowrap text-[10px] text-text-faint"
            style={{ left: (hour - first) * 60 * scale }}>
            {hourLabel(hour * 60)}
          </span>
        ))}
      </div>
    </div>
  )
}

/** Board 20 A: everyone going, side by side in time, and the chosen column. */
export function TimeFinder({
  people, date, start, minutes, free, onNext, nextAvailable,
}: {
  people: CalendarPersonDays[]
  date: string
  start: number
  minutes: number
  /** Per person, from `/activity/meeting-fit`. */
  free: Record<string, boolean>
  onNext: () => void
  nextAvailable: boolean
}) {
  const [box, width] = useWidth()
  const end = start + minutes
  const { first, last } = windowFor(7, 5, start, end)
  const labelWidth = 74
  const trackWidth = Math.max(120, width - labelWidth - 18)
  const scale = trackWidth / ((last - first) * 60)
  const x = (m: number) => labelWidth + (m - first * 60) * scale
  const starts = people.map((person) => minutesOf(person.days[0]?.start ?? '08:00'))
  const workStart = starts.length > 0 ? Math.min(...starts) : 8 * 60
  const freeCount = people.filter((person) => free[person.id]).length
  const clip = (m: number) => Math.min(Math.max(m, first * 60), last * 60)

  return (
    <div className="rounded-[10px] border border-line-faint bg-surface-alt px-3 pb-1.5 pt-2.5">
      <div className="flex items-center gap-2">
        <b className="whitespace-nowrap text-[12px]">Everyone’s {weekdayLong(date)}</b>
        <span className="truncate text-[11.5px] text-text-muted">busy · the drive before each stop, estimated</span>
        <span className="flex-1" />
        <button type="button" onClick={onNext} disabled={!nextAvailable}
          className="whitespace-nowrap text-[11.5px] font-semibold text-accent disabled:opacity-50">
          Next free time ›
        </button>
      </div>
      <div ref={box} className="relative pb-[18px] pt-[26px]">
        {workStart > first * 60 && (
          <div className="absolute bottom-[18px] top-[26px]"
            style={{ left: labelWidth, width: (clip(workStart) - first * 60) * scale, background: BEFORE_WORK }}>
            <span className="absolute bottom-1 left-1.5 whitespace-nowrap text-[9.5px] font-semibold text-text-faint">before work</span>
          </div>
        )}
        {people.map((person) => {
          const tone = toneOf(person.colour)
          const day = person.days[0]
          return (
            <div key={person.id} className="flex h-7 items-center gap-2">
              <div className="flex w-[66px] shrink-0 items-center gap-1.5 whitespace-nowrap text-[11.5px] font-semibold">
                <Face person={person} size={20} />
                <span>{person.firstName}</span>
              </div>
              <div className="relative h-7 border-b border-line-faint" style={{ width: trackWidth }}>
                {busyItems(person).filter((item) => item.end > first * 60 && item.start < last * 60).map((item) => {
                  const drive = day?.drives.find((one) => one.key === item.key)
                  const driveStart = drive && drive.minutes > 0 ? clip(item.start - drive.minutes) : null
                  return (
                    <span key={item.key}>
                      {driveStart !== null && clip(item.start) > driveStart && (
                        <div className="absolute top-[5px] h-[18px] opacity-60"
                          style={{ left: (driveStart - first * 60) * scale, width: (clip(item.start) - driveStart) * scale, background: DRIVE_STRIPES }} />
                      )}
                      <div className="absolute top-[3px] h-[22px] overflow-hidden whitespace-nowrap rounded-[5px] px-[5px] text-[10px] font-semibold leading-[22px] text-text-body"
                        style={{ left: (clip(item.start) - first * 60) * scale, width: Math.max(14, (clip(item.end) - clip(item.start)) * scale - 2),
                          background: tone.tint, borderLeft: `3px solid ${tone.edge}` }}>
                        {item.kind === 'block' && item.blockType ? BLOCK_TYPE_LABELS[item.blockType as BlockType] ?? item.title : shortStopName(item.title)}
                      </div>
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })}
        <div className="pointer-events-none absolute bottom-[18px] top-[22px] rounded-[7px] border-2 border-accent"
          style={{ left: x(clip(start)), width: Math.max(8, (clip(end) - clip(start)) * scale), background: 'rgb(var(--color-accent) / .08)' }} />
        <div className="absolute top-1.5 whitespace-nowrap text-[10.5px] font-bold text-accent" style={{ left: x(clip(start)) }}>
          {finderWords(freeCount, people.length, start, end)}
        </div>
        {Array.from({ length: last - first + 1 }, (_, n) => first + n).map((hour) => (
          <span key={hour} className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-[10px] text-text-faint"
            style={{ left: labelWidth + (hour - first) * 60 * scale }}>
            {hourLabel(hour * 60)}
          </span>
        ))}
      </div>
    </div>
  )
}

/** The people's day (`GET /activity/free`) and the next span all of them are free. */
export interface FreeAnswer {
  date: string
  free: { from: string; to: string; start: number; end: number }[]
  next: { date: string; start: number; end: number } | null
  people: CalendarPersonDays[]
}

export function useFree(people: string[], date: string | null, minutes: number, after: number, request: <T>(path: string) => Promise<T>): FreeAnswer | null {
  const [answer, setAnswer] = useState<FreeAnswer | null>(null)
  const key = `${people.join(',')}|${date}|${minutes}|${after}`
  useEffect(() => {
    if (!date || people.length === 0) { setAnswer(null); return undefined }
    let live = true
    const timer = window.setTimeout(() => {
      request<FreeAnswer>(`/activity/free?people=${people.join(',')}&date=${date}&minutes=${minutes}&after=${after}`)
        .then((found) => { if (live) setAnswer(found) })
        .catch(() => { if (live) setAnswer(null) })
    }, 120)
    return () => { live = false; window.clearTimeout(timer) }
  }, [key])
  return answer
}

/** `1:30 Dana` — the new block's own words in the strip. */
export const stripLabel = (start: number, who: string): string => `${clockBare(start)} ${who}`
